import { useRef, useEffect, useState } from 'react';
import { useBeatDetector } from '../../hooks';
import { Button } from '../ui';

export interface MusicUploaderProps {
    onMusicUpload: (file: File) => void;
}

export function MusicUploader({ onMusicUpload }: MusicUploaderProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const {
        isAnalyzing,
        error,
        beats,
        bpm,
        duration,
        waveformData,
        analyzeBeats,
    } = useBeatDetector();

    const [musicFile, setMusicFile] = useState<File | null>(null);

    // Draw waveform when data changes
    useEffect(() => {
        if (waveformData.length > 0 && canvasRef.current) {
            drawWaveform();
        }
    }, [waveformData, beats]);

    const handleFileSelect = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const file = files[0];
        if (!file.type.startsWith('audio/')) {
            alert('Vælg venligst en gyldig lydfil');
            return;
        }

        setMusicFile(file);

        try {
            await analyzeBeats(file);
            onMusicUpload(file);
        } catch (err) {
            console.error('Music upload error:', err);
            alert('Fejl ved analyse af musik: ' + (err instanceof Error ? err.message : 'Ukendt fejl'));
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const drawWaveform = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const width = canvas.width;
        const height = canvas.height;

        // Clear canvas
        ctx.fillStyle = '#13151c';
        ctx.fillRect(0, 0, width, height);

        // Draw waveform
        const gradient = ctx.createLinearGradient(0, 0, width, 0);
        gradient.addColorStop(0, '#667eea');
        gradient.addColorStop(0.5, '#764ba2');
        gradient.addColorStop(1, '#667eea');

        ctx.fillStyle = gradient;

        const barWidth = width / waveformData.length;
        const centerY = height / 2;

        waveformData.forEach((value: number, i: number) => {
            const barHeight = value * centerY * 0.9;
            const x = i * barWidth;

            // Draw bar (centered)
            ctx.fillRect(x, centerY - barHeight, barWidth - 1, barHeight * 2);
        });

        // Draw beat markers
        ctx.strokeStyle = '#00f2fe';
        ctx.lineWidth = 2;

        beats.forEach((beatTime: number) => {
            const x = (beatTime / duration) * width;

            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, height);
            ctx.stroke();
        });
    };

    return (
        <section className="bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-xl p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Baggrundsmusik</h3>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleUploadClick}
                    loading={isAnalyzing}
                    icon={
                        musicFile ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
                                <path d="M13 5L6 12L3 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
                                <path d="M8 12V4M8 4L5 7M8 4L11 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M2 12V13C2 14.1046 2.89543 15 4 15H12C13.1046 15 14 14.1046 14 13V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        )
                    }
                >
                    {isAnalyzing ? 'Analyserer...' : musicFile ? `${bpm} BPM - ${beats.length} Beats` : 'Upload Musik'}
                </Button>
            </div>

            {/* Waveform */}
            <div className="relative h-32 bg-gray-900 rounded-lg overflow-hidden">
                {waveformData.length > 0 ? (
                    <canvas
                        ref={canvasRef}
                        width={800}
                        height={128}
                        className="w-full h-full"
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <svg className="w-12 h-12 text-gray-600 mb-2" fill="none" viewBox="0 0 48 48">
                            <path d="M24 8V40M16 12V36M32 12V36M8 16V32M40 16V32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <p className="text-sm text-gray-500">Upload musik for at se waveform og beat markers</p>
                    </div>
                )}
            </div>

            {/* Error */}
            {error && (
                <div className="mt-4 p-3 bg-red-600/20 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400">{error}</p>
                </div>
            )}

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files)}
            />
        </section>
    );
}
